import * as I from 'immutable';
import throttle from 'lodash/throttle';
import React from 'react';

import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import * as geoJsonUtils from 'app/utils/geoJsonUtils';
import * as mapUtils from 'app/utils/mapUtils';

import {getLegendValueCoverageForPolygon} from './getLegendValueCoverage';

interface UseVisibleLegendValueCoverageParams {
  cursor: string | null | undefined;
  featureCollection: HydratedFeatureCollection;
  featureData: I.ImmutableListOf<ApiFeatureData>;
  firebaseToken: string | null;
  hasLayerLegendMap: boolean;
  legendSamplingLayerKey: string;
  map: mapboxgl.Map;
  polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon | null;
}

export function useVisibleLegendValueCoverage({
  cursor,
  featureCollection,
  featureData,
  firebaseToken,
  hasLayerLegendMap,
  legendSamplingLayerKey,
  map,
  polygon,
}: UseVisibleLegendValueCoverageParams) {
  const [isFilterActive, setIsFilterActive] = React.useState(true);
  const [isVisibleLegendValuesLoading, setIsVisibleLegendValuesLoading] = React.useState(false);
  const [visibleLegendValueCoverage, setVisibleLegendValueCoverage] = React.useState<Record<
    string,
    number
  > | null>(null);
  const legendFilterRequestIdRef = React.useRef(0);
  const legendFilterAbortControllerRef = React.useRef<AbortController | null>(null);

  const filterLegendValuesForCurrentView = React.useCallback(async () => {
    const requestId = ++legendFilterRequestIdRef.current;
    const isStale = () => requestId !== legendFilterRequestIdRef.current;

    if (!hasLayerLegendMap || !cursor) {
      return;
    }

    const mapBoundsBbox = mapUtils.getMapBoundsBbox(map);
    if (!mapBoundsBbox) {
      return;
    }

    const viewportBbox = geoJsonUtils.bboxPolygon(mapBoundsBbox).geometry;
    const viewportPolygon = polygon
      ? (geoJsonUtils.intersectGeometries(viewportBbox, polygon)?.geometry ?? viewportBbox)
      : viewportBbox;

    legendFilterAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    legendFilterAbortControllerRef.current = abortController;

    setIsVisibleLegendValuesLoading(true);
    try {
      const coverage = await getLegendValueCoverageForPolygon({
        cursor,
        featureCollection,
        featureData,
        firebaseToken,
        legendSamplingLayerKey,
        polygon: viewportPolygon,
        signal: abortController.signal,
      });

      if (abortController.signal.aborted || isStale()) {
        return;
      }

      setVisibleLegendValueCoverage(coverage);
    } catch (_error) {
      if (abortController.signal.aborted) {
        return;
      }
      setVisibleLegendValueCoverage(null);
    } finally {
      if (!isStale()) {
        setIsVisibleLegendValuesLoading(false);
      }

      if (legendFilterAbortControllerRef.current === abortController) {
        legendFilterAbortControllerRef.current = null;
      }
    }
  }, [
    cursor,
    featureCollection,
    featureData,
    firebaseToken,
    hasLayerLegendMap,
    legendSamplingLayerKey,
    map,
    polygon,
  ]);

  React.useEffect(() => {
    setVisibleLegendValueCoverage(null);
    setIsFilterActive(true);
  }, [cursor, legendSamplingLayerKey]);

  React.useEffect(() => {
    if (!hasLayerLegendMap || !isFilterActive) {
      legendFilterAbortControllerRef.current?.abort();
      legendFilterAbortControllerRef.current = null;
      setVisibleLegendValueCoverage(null);
      setIsVisibleLegendValuesLoading(false);
      return;
    }

    filterLegendValuesForCurrentView();

    const handler = throttle(filterLegendValuesForCurrentView, 500);

    map.on('moveend', handler);
    return () => {
      map.off('moveend', handler);
      handler.cancel();
    };
  }, [hasLayerLegendMap, isFilterActive, filterLegendValuesForCurrentView, map]);

  React.useEffect(() => {
    return () => {
      legendFilterAbortControllerRef.current?.abort();
      legendFilterAbortControllerRef.current = null;
    };
  }, []);

  return {
    isFilterActive,
    setIsFilterActive,
    isVisibleLegendValuesLoading,
    visibleLegendValueCoverage,
  };
}
